package net.frozenbit.games.soundgame.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.withTransform
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import kotlin.math.floor
import kotlin.math.roundToInt
import kotlin.math.sqrt

/**
 * Paints a seamless, deterministic collage of [bitmaps].
 *
 * Bitmaps are placed on a grid and enlarged before rotation so every tile is covered. Image
 * selection, rotation, and tile scale come from smooth value noise rather than per-frame random
 * values, keeping the collage stable across redraws.
 */
class PackedBitmapPainter(
    bitmaps: Set<ImageBitmap>,
    private val tileSize: Dp = 120.dp,
    private val maxRotationDegrees: Float = 18f,
    private val seed: Int = 0,
) : Painter() {
    private val bitmaps = bitmaps.toList()

    override val intrinsicSize: Size = Size.Unspecified

    init {
        require(this.bitmaps.isNotEmpty()) { "PackedBitmapPainter requires at least one bitmap." }
        require(tileSize.value > 0f) { "tileSize must be greater than zero." }
        require(maxRotationDegrees >= 0f) { "maxRotationDegrees cannot be negative." }
    }

    override fun DrawScope.onDraw() {
        val tileSide = tileSize.toPx()
        val columns = (size.width / tileSide).toInt() + 3
        val rows = (size.height / tileSide).toInt() + 3

        for (row in -1 until rows) {
            for (column in -1 until columns) {
                drawTile(
                    column = column,
                    row = row,
                    tileSide = tileSide,
                )
            }
        }
    }

    private fun DrawScope.drawTile(column: Int, row: Int, tileSide: Float) {
        val selection = valueNoise(column / 2f, row / 2f, seed)
        val bitmap = bitmaps[(selection * bitmaps.size).toInt().coerceIn(0, bitmaps.lastIndex)]
        val rotation = (valueNoise(column / 3f, row / 3f, seed + 1) - 0.5f) * maxRotationDegrees * 2f
        val scale = 0.9f + valueNoise(column / 4f, row / 4f, seed + 2) * 0.2f
        val center = Offset((column + 0.5f) * tileSide, (row + 0.5f) * tileSide)
        val diagonal = tileSide * sqrt(2f) * scale
        val aspectRatio = bitmap.width.toFloat() / bitmap.height
        val destinationSize = if (aspectRatio >= 1f) {
            Size(diagonal * aspectRatio, diagonal)
        } else {
            Size(diagonal, diagonal / aspectRatio)
        }

        withTransform({
            rotate(degrees = rotation, pivot = center)
        }) {
            drawImage(
                image = bitmap,
                dstOffset = IntOffset(
                    x = (center.x - destinationSize.width / 2f).roundToInt(),
                    y = (center.y - destinationSize.height / 2f).roundToInt(),
                ),
                dstSize = IntSize(
                    width = destinationSize.width.roundToInt(),
                    height = destinationSize.height.roundToInt(),
                ),
            )
        }
    }
}

@Composable
fun rememberPackedBitmapPainter(
    bitmaps: Set<ImageBitmap>,
    tileSize: Dp = 120.dp,
    maxRotationDegrees: Float = 18f,
    seed: Int = 0,
): Painter = remember(bitmaps, tileSize, maxRotationDegrees, seed) {
    PackedBitmapPainter(
        bitmaps = bitmaps,
        tileSize = tileSize,
        maxRotationDegrees = maxRotationDegrees,
        seed = seed,
    )
}

private fun valueNoise(x: Float, y: Float, seed: Int): Float {
    val x0 = floor(x).toInt()
    val y0 = floor(y).toInt()
    val xFraction = x - x0
    val yFraction = y - y0
    val xSmooth = smoothStep(xFraction)
    val ySmooth = smoothStep(yFraction)
    val top = interpolate(hash(x0, y0, seed), hash(x0 + 1, y0, seed), xSmooth)
    val bottom = interpolate(hash(x0, y0 + 1, seed), hash(x0 + 1, y0 + 1, seed), xSmooth)

    return interpolate(top, bottom, ySmooth)
}

private fun smoothStep(value: Float): Float = value * value * (3f - 2f * value)

private fun interpolate(start: Float, end: Float, amount: Float): Float = start + (end - start) * amount

private fun hash(x: Int, y: Int, seed: Int): Float {
    var value = x * 0x1f1f1f1f + y * 0x5f356495 + seed * 0x6c8e9cf5
    value = (value xor (value ushr 16)) * 0x45d9f3b
    value = (value xor (value ushr 16)) * 0x45d9f3b
    value = value xor (value ushr 16)

    return (value.toLong() and 0xffff_ffffL).toFloat() / 0x1_0000_0000L.toFloat()
}
